<template>
  <div>
    <b-card>
        <template slot="header">
            <h4 class="mt-2 custom-card-header-text">Requests Approval</h4>
            <ul class="list-inline float-right">
              <li v-if="communities.length>1" class="list-inline-item"><label for="communityFilter" class="custom-card-header-filter">Community:</label></li>
              <li v-if="communities.length>1" class="list-inline-item">
                  <b-form-select v-model="selected" v-on:change="filterByCommunity" class="custom-card-header-filter">
                      <option :value="null" selected disabled>Community</option>
                      <option value="all">All</option>
                      <option v-for="community in communities" v-bind:value="community._id">{{community.name}}</option>
                  </b-form-select>
              </li>
              <li v-if="circles.length>=1" class="list-inline-item"><label for="circleFilter" class="custom-card-header-filter">Circle:</label></li>
              <li v-if="circles.length>=1" class="list-inline-item">
                <b-form-select v-model="selectedCircle" @change="filterByCircle" class="custom-card-header-filter" id="circleFilter">
                  <option :value="null" selected disabled>Circle</option>
                  <option value="all">All</option>
							    <option value="none">No Circle</option>
                  <option v-for="(circle, idx) in circles" :key="`${idx}$-{circle.short}`" v-bind:value="circle.short" v-if="circle.name && circles.length>=1" >{{ circle.name}}</option>
                </b-form-select>
              </li>
              <li class="list-inline-item">
                <button type="button" aria-label="Export" aria-live="assertive" v-if="exportBtn" @click="exportData()" title="Download request approval data" class="btn custom-card-header-filter btn-dropbox tab-focus"><i class="icon-cloud-download"></i></button>
                <button type="button" v-if="processBtn" v-b-tooltip.hover.bottom title="Generating report" class="btn custom-card-header-filter btn-dropbox"><i class="fa fa-spinner fa-spin"></i></button>
              </li>
            </ul>
        </template>
        <b-card-body>
            <div class="toverflow-unset">
                <search-section :show-approval-dropdown="authUser.role === 'SuperAdmin'" search-placeholder="Search Requests..." :selected-approval-type="selectedApprovalType" @search="handleSearch" @limit-change="handleLimitChange" @approval-type-change="handleApprovalTypeChange"/>
                <v-client-table ref="approvalsTable" v-bind:data="requests" v-bind:columns="columns" :options="options" id="dataTable" class="request-approval-table">
                    <span slot="member" slot-scope="props" class="display-link" @click="redirectUrls(props.row.memberId)" v-if="props.row.memberId">
                      {{props.row.member}}
                    </span>
                    <span slot="member" slot-scope="props" v-else-if = "props.row.member">
                      {{props.row.member}}
                    </span>
                    <span slot="subject" slot-scope="props">
                      {{props.row.subject ? props.row.subject : 'N/A'}}
                    </span>
                    <span slot="community" slot-scope="props">
                      {{props.row.communityName ? props.row.communityName : 'N/A'}}
                    </span>
                    <span slot="date" slot-scope="props" v-if="props.row.date">
                      {{ props.row.date | momentDate }}
                    </span>
                    <span slot="date" v-else>N/A</span>
                    <span slot="action" slot-scope="props" class="res-req-action-width">
                      <div class="action-cell-stack">
                        <div class="action-cell-item">
                          <b-dropdown v-if="authUser.memberID || authUser.role =='SuperAdmin'" v-b-tooltip.hover.bottom title="Take action" variant="btn btn-dropbox btn-sm tab-focus" right>
                            <template slot="button-content">
                              <i class="icon-settings"></i>
                            </template>
                            <b-dropdown-item @click="openRequestInNewTab(props.row.requestId)">View Request</b-dropdown-item>
                            <b-dropdown-item @click="approveRequest(props.row.requestId, 'approve', 'no')">Approve request</b-dropdown-item>
                            <b-dropdown-item @click="approveRequest(props.row.requestId, 'approve', 'yes')">Approve member</b-dropdown-item>
                            <b-dropdown-item @click="$router.push({ path: `/engagement/requests-coach/${props.row.requestId}`, query: { from: 'request-approval-page' } })">Coach request</b-dropdown-item>
                            <b-dropdown-item @click="approveRequest(props.row.requestId, 'reject', 'no')">Reject and delete</b-dropdown-item>
                          </b-dropdown>
                        </div>
                        <div class="action-cell-item">
                          <button type="button" class="eval-status-btn" v-b-tooltip.hover.bottom title="View Request Evaluation"
                            @click="handleLightbulbClick(props.row)">
                            <span class="eval-icon eval-icon-interactive" :class="getStatusIconClass(props.row)">
                              <i :class="getStatusIcon(props.row)"></i>
                            </span>
                          </button>
                        </div>
                      </div>
                    </span>
                </v-client-table>
            </div>
        </b-card-body>
        <b-modal id="request-evaluation-modal" v-model="showEvaluationModal" hide-header hide-footer centered modal-class="evaluation-modal">
          <div class="eval-card">
            <!-- Header -->
            <div class="eval-header d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <span class="eval-icon" :class="headerIconClass">
                  <i :class="headerIcon"></i>
                </span>
                <span class="eval-title">Request Evaluation</span>
              </div>
              <div class="d-flex align-items-center">
                <span v-if="hasEvaluation" class="eval-status-badge" :class="statusBadgeClass">{{ statusLabel }}</span>
                <button type="button" class="eval-close ml-3" aria-label="Close" @click="showEvaluationModal = false">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>

            <hr class="eval-divider" />

            <!-- LOADING -->
            <div v-if="isEvaluating" class="d-flex flex-column align-items-center justify-content-center py-4">
              <b-spinner small variant="light" />
              <p class="eval-muted mt-2 mb-0">Analyzing request...</p>
            </div>

            <!-- ERROR -->
            <div v-else-if="selectedEvaluation && selectedEvaluation.error" class="text-center py-3">
              <p class="eval-text mb-2">{{ selectedEvaluation.message }}</p>
              <button type="button" class="eval-btn eval-btn-primary" @click="generateEvaluation">Try again</button>
            </div>

            <!-- HAS EVALUATION -->
            <div v-else-if="hasEvaluation">
              <p class="eval-reason-label mb-2">REASON</p>
              <p class="eval-reason mb-3">{{ summaryText }}</p>

              <div class="d-flex align-items-center">
              <button type="button" class="eval-btn eval-btn-primary flex-grow-1" @click="showEvaluationModal = false">
                Close
              </button>
              </div>
            </div>

            <!-- CANNOT EVALUATE -->
            <div v-else-if="!canEvaluateSelected" class="text-center py-3">
              <p class="eval-muted mb-0">This request has no text to evaluate.</p>
            </div>

            <!-- NO EVALUATION -->
            <div v-else class="text-center py-3">
              <p class="eval-muted mb-3">No evaluation generated yet.</p>
              <button type="button" class="eval-btn eval-btn-primary" @click="generateEvaluation">Generate evaluation</button>
            </div>
          </div>
        </b-modal>
    </b-card>
  </div>
</template>

<script>
import { shuffleArray } from '@/shared/utils'
import axios from 'axios'
import { exportCSV } from '@/assets/js/common';
import { setRequestStatus } from '@/assets/js/set-status';
var moment = require('moment-timezone');
import VueNotifications from 'vue-notifications';
import SearchSection from '@/views/engagement/components/searchSection.vue';
import { interpretEvaluation, severityToLabel, severityToIcon, severityToClass } from '@/shared/evaluationUtils';


export default {
  name: 'requests',
	components: {
		SearchSection
	},
  computed: {
    columns() {
      //Hide the column of community from the table depending on the user role
      if (this.authUser.role !== 'SuperAdmin') {
        return this.originalColumns.filter(col => col !== 'community');
      }
      return this.originalColumns;
    },
    interpretedEvaluation() {
      return this.interpretEvaluation(this.selectedEvaluation);
    },
    hasEvaluation() {
      const s = this.interpretedEvaluation.severity;
      return s === 'safe' || s === 'caution' || s === 'danger';
    },
    summaryText() {
      const e = this.selectedEvaluation;
      if (!e) return '';
      if (e.summary && typeof e.summary === 'object') return e.summary.summary_of_results || '';
      if (typeof e.summary === 'string') return e.summary;
      return e.overall_explanation || '';
    },
    statusLabel() {
      return this.severityToLabel(this.interpretedEvaluation.severity);
    },
    statusBadgeClass() {
      return this.severityToBadgeClass(this.interpretedEvaluation.severity);
    },
    headerIcon() {
      return this.severityToIcon(this.interpretedEvaluation.severity);
    },
    headerIconClass() {
      return this.severityToIconClass(this.interpretedEvaluation.severity);
    },
    canEvaluateSelected() {
      if (!this.selectedRow) return false;
      const subject = this.selectedRow.subject || '';
      const text = this.selectedRow.text || '';
      return !!(subject || text).trim();
    }
  },
  data() {
    return {
      selectedRow: null,
      selectedEvaluation: null,
      showEvaluationModal: false,
      isEvaluating: false,
      selectedApprovalType: null,
      requestsBackup: [],
      requests: [],
      communities: [],
			circles: [],
			selected: "all",
			selectedCircle: "all",
      originalColumns: ['date', 'member', 'circleName',  'community', 'subject', 'text', 'action'],
      options: {
          headings: {
            member: 'Name',
            circleName: 'Circle'
          },
          sortable: ['date', 'member', 'circleName', 'subject'],
          sortIcon: { base:'fa', up:'fa-sort-asc', down:'fa-sort-desc', is:'fa-sort' },
          pagination: {
              show: false,
              dropdown: false,
              chunk: 10,
              nav: 'fixed'
          },
          filterable: true,
          perPageDropdown: []
      },
      token: '',
      incrementId: 0,
			exportBtn: true,
			processBtn: false,
    }
  },
  notifications: {
		showWarnMsg: {
			type: VueNotifications.types.warn,
			message: "No data found for export file"
		},
		showDownloadingMsg: {
			type: VueNotifications.types.success,
			message: "Export request sent.  Data is now downloading."
		},
      showSuccessMsg: {
          type: VueNotifications.types.success
      }
  },
  async created() {
    this.token = localStorage.getItem('token');
    const user = localStorage.getItem('authUser');
    if(user) {
        this.authUser = JSON.parse(user);
    }
    if (this.authUser.role === 'SuperAdmin') {
      this.selectedApprovalType = 'Protopia';
    } else if (this.authUser.role === 'Admin') {
      this.selectedApprovalType = 'Customer';
    } else {
      this.selectedApprovalType = 'All';
    }
    await this.getCommunity();
    //await this.loadData();
  },
  methods: {
    /*
    loadData() {
      var url = '';
      if(this.selected) {
        url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval/?community_id=${this.selected}`;
      } else {
        url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval`;
      }
      this.axios.get(url, {headers: {'x-access-token': this.token}}).then(async response => {
        if (response.data.reqApproval) {
          this.requests = response.data.reqApproval;
          this.requestsBackup = [...response.data.reqApproval]; //Keep the original requests;
        }
      }).catch(err => {
        if (err.response.status  === 401) {
          localStorage.removeItem('authUser');
          this.$router.push('/login');
        }
      });
      this.$helpers.setTabindexOnTableCells();
    },*/
    checkData(value) {
      if(value) { return value; } else { return 'N/A';}
    },
    async getCommunity() {
      const {data} = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/filter-community`, {headers: {'x-access-token': this.token}})
      .catch(err => {
        if (err.response.status  === 401) {
          localStorage.removeItem('authUser');
          this.$router.push('/login');
        }
      });
      this.communities = data;
      if(this.$route.params.community_id) {
        var selectedCommunity = data.filter(community => community._id == this.$route.params.community_id);
        if (selectedCommunity.length <= 1) {
					this.selected = selectedCommunity[0]._id;
				}
      } else {
				if (this.communities.length <= 1) {
					this.selected = this.communities[0]._id;
				}
			}
			this.filterByCommunity();
    },
    filterByCommunity: async function(value) {
      this.selectedCircle = "all";
			if (this.selected == "all") {
				this.circles = [];
        const {data} = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval/`, {headers: {'x-access-token': this.token}})
        .catch(err => {
            if (err.response.status  === 401) {
                localStorage.removeItem('authUser');
                this.$router.push('/login');
            }
        });
        if (data.reqApproval) {
          this.requests = data.reqApproval;
          this.requestsBackup = [...this.requests];
          this.applyApprovalFilter();
        }
      } else {
        this.circles = this.communities.find(c => c._id == this.selected).additionalParams;
				this.circles.sort(function (a, b) {
					return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
				});
        const {data} = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval/?community_id=${this.selected}`, {headers: {'x-access-token': this.token}})
        .catch(err => {
            if (err.response.status  === 401) {
                localStorage.removeItem('authUser');
                this.$router.push('/login');
            }
        });
        if (data.reqApproval) {
          this.requests = data.reqApproval;
          this.requestsBackup = [...this.requests];
          this.applyApprovalFilter();
        }
      }
    },
		filterByCircle: async function (value) {
			if (this.selectedCircle == "all") {
				const {data} = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval/?community_id=${this.selected}`, {headers: {'x-access-token': this.token}})
        .catch(err => {
            if (err.response.status  === 401) {
                localStorage.removeItem('authUser');
                this.$router.push('/login');
            }
        });
        if (data.reqApproval) {
          this.requests = data.reqApproval;
          this.requestsBackup = [...this.requests];
          this.applyApprovalFilter();
        }
			} else {
				const {data} = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval/?community_id=${this.selected}&circle=${this.selectedCircle}`, {headers: {'x-access-token': this.token}})
        .catch(err => {
            if (err.response.status  === 401) {
                localStorage.removeItem('authUser');
                this.$router.push('/login');
            }
        });
        if (data.reqApproval) {
          this.requests = data.reqApproval;
          this.requestsBackup = [...this.requests];
          this.applyApprovalFilter();
        }
			}
		},
    setStatus: function(status) {
        return setRequestStatus(status);
    },
    exportData: async function () {
        this.showDownloadingMsg();
				this.exportBtn = false;
				this.processBtn = true;
				let url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/request/approval/?community_id=${this.selected ? this.selected : ''}&circle=${this.selectedCircle ? this.selectedCircle : ''}`;
				let exculdedColumns = [];
				await this.axios.get(url, {headers: {'x-access-token': this.token}}).then(async response => {
					if (response.data.reqApproval && response.data.reqApproval.length) {
						const reqApproval = this.filterByApprovalType(response.data.reqApproval);
						if (reqApproval.length) {
							await exportCSV(reqApproval.slice(), 'requestApprovals');
						} else {
							this.showWarnMsg();
						}
						this.exportBtn = true;
						this.processBtn = false;
					} else {
						this.showWarnMsg();
						this.exportBtn = true;
						this.processBtn = false;
					}
				}).catch(err => {
					if (err.response.status === 401) {
						localStorage.removeItem('authUser');
						this.$router.push('/login');
					}
				});
		},
    redirectUrls: function(path) {
        window.open((`/engagement/members/${path}`), '_blank');
    },
    approveRequest: function(askId, action, status) {
        let params = {
            askId: askId,
            action: action,
            status: status,
            approvedBy: 'dashboard'
        }
        if (this.authUser.memberID) {
            params.adminId = this.authUser.memberID;
        }
        this.axios.post(`${process.env.VUE_APP_API_BASE_URL}/dashboard/request-approval`, params, {headers: {'x-access-token': this.token}}).then(async response => {
            if (action == 'approve') {
                this.showSuccessMsg({message: 'Thank you for approving the request.'})
                this.announcement('Thank you for approving the request.')
            } else if (action == 'reject') {
                this.showSuccessMsg({message: 'Thank you for your response'})
                this.announcement('Thank you for your response');
            }
            await this.loadData();
        }).catch(err => {
            if (err.response.status  === 401) {
                localStorage.removeItem('authUser');
                this.$router.push('/login');
            }
        });
    },
    openRequestInNewTab(path) {
      	window.open((`/engagement/requests/${path}`), '_blank');
    },
    announcement(message) {
            const liveRegion = document.createElement('div');
            liveRegion.setAttribute('aria-live', 'polite');
            liveRegion.setAttribute('role', 'alert');
            liveRegion.innerText = message;
            document.body.appendChild(liveRegion);
            setTimeout(() => {
                liveRegion.remove();
            },1000);
    },
		handleSearch(searchText) {
			// Use the search text from the child component to filter your table
			this.$refs.approvalsTable.setFilter(searchText);
		},
		handleLimitChange(newLimit) {
			// Use the new limit value from the child to update your table
			this.$refs.approvalsTable.setLimit(newLimit);
    },
    // Shared with exportData() so the Protopia/Customer/All approval-type filter
    // narrowing the on-screen table can't silently drift out of sync with what
    // gets exported — both call this same predicate over their own list.
    filterByApprovalType(list) {
      if (!list || !list.length) return list || [];
      //Reset to all requests
      if (this.selectedApprovalType === 'All' || !this.selectedApprovalType) {
        return [...list];
      }

      //Lookup of communities by ID
      const communityById = new Map(
        this.communities.map(c => [c._id.toString(), c])
      );

      //Determine if the community has the management approval set for requests
      const hasRequestApproval = (req) => {
        if (!req.community) return false;
        const community = communityById.get(req.community.toString());
        if (!community || !community.mgmtApproval) return false;
        return community.mgmtApproval.request === true;
      };

      //Filter based on the selected approval type.
      if (this.selectedApprovalType === "Protopia") {
        //Filter requests that have the super admin approval set to true
        return list.filter(req => hasRequestApproval(req));
      } else if (this.selectedApprovalType === "Customer") {
        //Filter requests that don't have the super admin approval set
        return list.filter(req => !hasRequestApproval(req));
      }
      return [...list];
    },
    applyApprovalFilter() {
      if (!this.requestsBackup || !this.requestsBackup.length) return;
      this.requests = this.filterByApprovalType(this.requestsBackup);
    },
    handleApprovalTypeChange(approvalType) {
      this.selectedApprovalType = approvalType;
      this.applyApprovalFilter();
    },
    handleLightbulbClick(row) {
      this.selectedRow = row;
      this.selectedEvaluation = row.requestEvaluation || null;
      this.isEvaluating = false;
      this.showEvaluationModal = true;
    },
    async generateEvaluation() {
      if (!this.selectedRow) return;
      const subject = this.selectedRow.subject || '';
      const text = this.selectedRow.text || '';
      const message = [subject, text].filter(Boolean).join('\n\n').trim();
      if (!message) return;

      this.isEvaluating = true;
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_BASE_URL}/dashboard/evaluate_request`, {
          request_message: message,
          requestId: this.selectedRow.requestId
        }, {
          headers: { 'x-access-token': this.token }
        });

        this.selectedEvaluation = res.data;
        this.selectedRow.requestEvaluation = res.data;
      } catch (err) {
        console.error('Evaluation failed:', err);
        this.selectedEvaluation = { error: true, message: 'Evaluation unavailable' };
        this.selectedRow.requestEvaluation = this.selectedEvaluation;
      } finally {
        this.isEvaluating = false;
      }
    }, interpretEvaluation(evaluation) {
      return { severity: interpretEvaluation(evaluation) };
    },
    severityToLabel(severity) {
      return severityToLabel(severity);
    },
    severityToBadgeClass(severity) {
      const c = severityToClass(severity);
      if (c === 'safe') return 'eval-status-pass';
      if (c === 'danger') return 'eval-status-fail';      
      if (c === 'caution') return 'eval-status-caution';
      return 'eval-status-muted';
    },
    severityToIconClass(severity) {
      const c = severityToClass(severity);
      if (c === 'safe') return 'eval-icon-pass';
      if (c === 'danger') return 'eval-icon-fail';  
      if (c === 'caution') return 'eval-icon-caution';
      return 'eval-icon-muted';
    },
    severityToIcon(severity) {
      return severityToIcon(severity);
    },

    getStatusIcon(row) {
      return this.severityToIcon(this.interpretEvaluation(row && row.requestEvaluation).severity);
    },
    getStatusIconClass(row) {
      return this.severityToIconClass(this.interpretEvaluation(row && row.requestEvaluation).severity);
    },
  }
}
</script>

<style lang="scss">
.evaluation-modal {
  .modal-dialog {
    max-width: 460px; }
  .modal-content {
    background: transparent;
    border: none;
    box-shadow: none;
  }
  .modal-body { padding: 0; }
}

.action-cell-stack {
  display: block;
  width: 100%;
}
.action-cell-item {
  display: block;
  width: -moz-fit-content;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;

  & + & { margin-top: 14px; }
}

.eval-status-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: transparent !important;
  line-height: 0;
  cursor: pointer;

  &:focus,
  &:focus-visible,
  &:active,
  &.focus,
  &:hover {
    outline: none;
    box-shadow: none;
    text-decoration: none;
    background: transparent;
  }

  &:hover .eval-icon-interactive {
    transform: scale(1.08);
    filter: brightness(1.15);
  }
}

.eval-icon-interactive {
  cursor: pointer;
  transition: transform 0.15s ease, filter 0.15s ease, background 0.15s ease;
}

.eval-card {
  background: #2b3140;
  color: #e8eaed;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}

.eval-header { margin-bottom: 12px; }

.eval-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  border: 1.5px solid transparent;

  i {
    line-height: 1;
    display: inline-block;
    transform: translateY(1px);
  }
}
.eval-icon-pass {
  color: #22c55e;
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.12);
}
.eval-icon-fail {
  color: #ef4444;
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.12);
}
.eval-icon-caution {
  color: #f59e0b;
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.12);
}
.eval-icon-muted {
  color: #9ca3af;
  border-color: #6b7280;
  background: rgba(156, 163, 175, 0.1);
}

.eval-title {
  font-weight: 600;
  font-size: 1rem;
  color: #f3f4f6;
  margin-left: 10px;
}

.eval-status-badge {
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  padding: 4px 12px;
  border-radius: 999px;
  display: inline-block;
  line-height: 1.2;
}
.eval-status-pass {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.15);
}
.eval-status-fail {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}
.eval-status-caution {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.15);
}
.eval-status-muted {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.12);
}

.eval-close {
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 1rem;
  padding: 0;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  &:hover { color: #f3f4f6; }
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
    color: #f3f4f6;
  }
}

.eval-divider {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 8px 0 16px;
}

.eval-reason-label {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: #9ca3af;
  font-weight: 600;
}

.eval-reason {
  color: #d1d5db;
  font-size: 0.9rem;
  line-height: 1.45;
}

.eval-text { color: #e8eaed; }
.eval-muted { color: #9ca3af; font-size: 0.875rem; }

.eval-btn {
  border: none;
  border-radius: 999px;
  padding: 8px 18px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.eval-btn-primary {
  background: #3b9eff;
  color: #fff;
  &:hover { background: #2f8ce8; }
}
.eval-btn-icon {
  background: #1f242f;
  color: #e8eaed;
  width: 38px;
  height: 38px;
  padding: 0;
  &:hover { background: #2a313e; }
}
</style>
